/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import {
  Alert,
  Box,
  Button,
  Chip,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CustomFilterV2, EmptyState } from '../../../components/v2';
import {
  tableV2AreaSx,
  tableV2ContainerSx,
  tableV2MultilineGridSx,
} from '../../../components/v2/tableV2Styles';
import { canViewVeiculoReport } from '../../../helpers/veiculoReportPermission';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { findVeiculosOptions } from '../../../store/veiculo/actions';
import { getVeiculosOptions } from '../../../store/veiculo/reducer';
import BaseService from '../../../services/BaseService';
import TableService from '../../../services/TableService';
import ChartsRow from './ChartsRow';
import KpiStrip from './KpiStrip';
import SegmentDimensionTabs from './SegmentDimensionTabs';
import SegmentFilterChips from './SegmentFilterChips';
import SegmentViewDescription from './SegmentViewDescription';
import VeiculoRankingSection from './VeiculoRankingSection';
import VeiculoSummary from './VeiculoSummary';
import {
  DIMENSION_FINALIDADE,
  DIMENSION_GERAL,
  DIMENSION_TO_SEGMENTOS_KEY,
  SEGMENT_ALL,
  getSegmentFilterLabel,
  isModularDimension,
  isSegmentFilterActive,
} from './segmentConstants';
import {
  EMPTY_REPORT,
  buildSearchFromFilter,
  computeParetoInsight,
  filtersEqual,
  formatPeriodLabel,
  getDefaultFilter,
  getScopeLabel,
  hasFilterSearchParams,
  isFleetScope,
  isPeriodInvalid,
  normalizeBiResponse,
  buildVeiculosByPlaca,
  filterSegmentsWithCost,
  normalizeFilterForApi,
  parseFilterFromSearch,
  resolveRanking,
  resolveSegmentChipOptions,
} from './constants';
import {
  formatTransactionDate,
  renderCategoriaCell,
  renderDescricaoCell,
  renderValorCell,
} from './tableCells';

const RelatorioVeiculoV2 = ({
  history,
  location,
  authData,
  moduleConfigurations,
  veiculosOptions,
  findVeiculosOptions,
}) => {
  const canView = canViewVeiculoReport({ authData, moduleConfigurations });
  const [localFilter, setLocalFilter] = useState(() =>
    parseFilterFromSearch(location.search, getDefaultFilter()),
  );
  const [reportData, setReportData] = useState(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [activeCategoria, setActiveCategoria] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const [veiculoNotFoundError, setVeiculoNotFoundError] = useState(null);
  const [segmentOptionsCache, setSegmentOptionsCache] = useState({});
  const [finalidadeOptions, setFinalidadeOptions] = useState([]);
  const skipUrlSyncRef = useRef(false);
  const transacoesSectionRef = useRef(null);
  const fleetContextRef = useRef({ dim: DIMENSION_GERAL, seg: SEGMENT_ALL });
  const localFilterRef = useRef(localFilter);
  localFilterRef.current = localFilter;

  const syncUrlFromFilter = useCallback(
    (filter, { replace = true } = {}) => {
      const search = buildSearchFromFilter(filter);
      const pathname = location.pathname;
      const nextUrl = search ? `${pathname}?${search}` : pathname;
      const currentUrl = location.search ? `${pathname}${location.search}` : pathname;
      if (nextUrl === currentUrl) return;

      skipUrlSyncRef.current = true;
      if (replace) {
        history.replace(nextUrl);
      } else {
        history.push(nextUrl);
      }
    },
    [history, location.pathname, location.search],
  );

  useEffect(() => {
    findVeiculosOptions(true);
  }, [findVeiculosOptions]);

  useEffect(() => {
    let cancelled = false;
    BaseService.get({ url: 'options/VEICULO_FINALIDADES' })
      .then((response) => {
        if (cancelled) return;
        const items = (response?.data || []).map((o) => ({
          key: o.id,
          label: o.value,
        }));
        setFinalidadeOptions(items);
      })
      .catch(() => {
        if (!cancelled) setFinalidadeOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }

    if (!hasFilterSearchParams(location.search)) {
      const defaults = getDefaultFilter();
      syncUrlFromFilter(defaults);
      if (!filtersEqual(defaults, localFilterRef.current)) {
        setLocalFilter(defaults);
        setActiveCategoria(null);
        setReload((prev) => !prev);
      }
      return;
    }

    const fromUrl = parseFilterFromSearch(location.search, getDefaultFilter());

    if (!filtersEqual(fromUrl, localFilterRef.current)) {
      setLocalFilter(fromUrl);
      setActiveCategoria(null);
      setReload((prev) => !prev);
    }
  }, [location.search, syncUrlFromFilter]);

  const apiFilter = useMemo(
    () => normalizeFilterForApi(localFilter),
    [localFilter],
  );

  const veiculoPlacaFallback = useMemo(() => {
    if (!localFilter.veiculoId) return null;
    const match = (veiculosOptions || []).find((v) => v.id === localFilter.veiculoId);
    return match?.placa || null;
  }, [localFilter.veiculoId, veiculosOptions]);

  const isFleetView = isFleetScope(localFilter);
  const showVeiculoSummary = Boolean(localFilter.veiculoId);

  const contextLabel = useMemo(() => {
    const placa = reportData.veiculo?.placa ?? veiculoPlacaFallback;
    const parts = [getScopeLabel(placa)];
    const periodo = formatPeriodLabel(localFilter.inicio, localFilter.fim);
    if (periodo) {
      parts.push(`Período: ${periodo}`);
    }
    return parts.join(' · ');
  }, [
    reportData.veiculo,
    veiculoPlacaFallback,
    localFilter.inicio,
    localFilter.fim,
  ]);

  const fetchReport = useCallback(async () => {
    if (!canView) return;

    if (!apiFilter) {
      setReportData(EMPTY_REPORT);
      setFetchError(false);
      return;
    }

    setLoading(true);
    setFetchError(false);
    try {
      const response = await TableService.request('reports/veiculo/bi', apiFilter);
      const data = normalizeBiResponse(response?.data);
      setReportData(data);

      const dim = localFilterRef.current.dim || DIMENSION_GERAL;
      const seg = localFilterRef.current.seg || SEGMENT_ALL;
      if (
        !localFilterRef.current.veiculoId &&
        isModularDimension(dim) &&
        seg === SEGMENT_ALL
      ) {
        const segmentKey = DIMENSION_TO_SEGMENTOS_KEY[dim];
        const items = data.segmentos?.[segmentKey];
        if (segmentKey && items?.length) {
          setSegmentOptionsCache((prev) => ({ ...prev, [dim]: items }));
        }
      }
    } catch {
      setReportData(EMPTY_REPORT);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [apiFilter, reload, canView]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const activeDim = localFilter.dim || DIMENSION_GERAL;
  const activeSeg = localFilter.seg || SEGMENT_ALL;
  const showSegmentChips = isFleetView && isModularDimension(activeDim);
  const showSegmentComparison =
    isFleetView && isModularDimension(activeDim) && activeSeg === SEGMENT_ALL;
  const showVehicleRanking = isFleetView;

  const veiculosByPlaca = useMemo(
    () => buildVeiculosByPlaca(veiculosOptions),
    [veiculosOptions],
  );

  const finalidadeLabels = useMemo(() => {
    const map = {};
    (finalidadeOptions || []).forEach((opt) => {
      if (opt.key) map[opt.key] = opt.label;
    });
    return map;
  }, [finalidadeOptions]);

  const fleetRankingForSegments = useMemo(
    () => resolveRanking(reportData.rankingVeiculos, reportData.transacoes),
    [reportData.rankingVeiculos, reportData.transacoes],
  );

  const segmentChipOptions = useMemo(() => {
    if (!isModularDimension(activeDim)) return [];
    return resolveSegmentChipOptions({
      dimension: activeDim,
      apiSegmentos: reportData.segmentos,
      cacheOptions: segmentOptionsCache[activeDim],
      ranking: fleetRankingForSegments,
      transacoes: reportData.transacoes,
      veiculosByPlaca,
      finalidadeLabels,
      extraStaticOptions:
        activeDim === DIMENSION_FINALIDADE ? finalidadeOptions : undefined,
    });
  }, [
    activeDim,
    segmentOptionsCache,
    reportData.segmentos,
    reportData.transacoes,
    fleetRankingForSegments,
    veiculosByPlaca,
    finalidadeLabels,
    finalidadeOptions,
  ]);

  const showEmptySegmentAlert =
    showSegmentChips && !loading && segmentChipOptions.length === 0;

  const activeSegmentLabel = useMemo(() => {
    if (!isSegmentFilterActive(activeDim, activeSeg)) return null;
    const match = segmentChipOptions.find(
      (opt) => String(opt.key) === String(activeSeg),
    );
    return match?.label || activeSeg;
  }, [activeDim, activeSeg, segmentChipOptions]);

  const segmentFilterLabel = useMemo(
    () => getSegmentFilterLabel(activeDim, activeSeg, activeSegmentLabel),
    [activeDim, activeSeg, activeSegmentLabel],
  );

  const segmentItems = useMemo(() => {
    if (!showSegmentComparison) return [];
    return filterSegmentsWithCost(segmentChipOptions);
  }, [showSegmentComparison, segmentChipOptions]);

  const fleetRanking = useMemo(
    () => (isFleetView ? fleetRankingForSegments : []),
    [isFleetView, fleetRankingForSegments],
  );

  const paretoInsight = useMemo(
    () => computeParetoInsight(fleetRanking, reportData.kpis?.totalGasto ?? 0),
    [fleetRanking, reportData.kpis?.totalGasto],
  );

  const onDimensionChange = useCallback(
    (dim) => {
      const nextFilter = {
        ...localFilterRef.current,
        dim,
        seg: SEGMENT_ALL,
      };
      setLocalFilter(nextFilter);
      syncUrlFromFilter(nextFilter);
      setActiveCategoria(null);
    },
    [syncUrlFromFilter],
  );

  const onSegmentChange = useCallback(
    (seg) => {
      const nextFilter = {
        ...localFilterRef.current,
        seg: seg || SEGMENT_ALL,
      };
      setLocalFilter(nextFilter);
      syncUrlFromFilter(nextFilter);
      setActiveCategoria(null);
      setReload((prev) => !prev);
    },
    [syncUrlFromFilter],
  );

  const onSelectSegment = useCallback(
    (segKey) => {
      if (!segKey || segKey === SEGMENT_ALL) {
        onSegmentChange(SEGMENT_ALL);
        return;
      }
      onSegmentChange(segKey);
    },
    [onSegmentChange],
  );

  const onClearSegmentFilter = useCallback(() => {
    onSegmentChange(SEGMENT_ALL);
  }, [onSegmentChange]);

  const onClearVeiculo = useCallback(() => {
    const nextFilter = {
      ...localFilterRef.current,
      veiculoId: null,
    };
    setLocalFilter(nextFilter);
    syncUrlFromFilter(nextFilter);
    setActiveCategoria(null);
    setReload((prev) => !prev);
  }, [syncUrlFromFilter]);

  const onSelectVeiculoByPlaca = useCallback(
    (placa, veiculoIdHint) => {
      if (!placa && !veiculoIdHint) return;

      let veiculoId = veiculoIdHint;
      if (!veiculoId && placa) {
        const normalized = placa.trim().toUpperCase();
        const match = (veiculosOptions || []).find(
          (v) => v.placa && v.placa.trim().toUpperCase() === normalized,
        );
        veiculoId = match?.id;
      }

      if (!veiculoId) {
        setVeiculoNotFoundError(`Veículo ${placa} não encontrado no cadastro.`);
        return;
      }

      if (!localFilter.veiculoId) {
        fleetContextRef.current = {
          dim: localFilter.dim || DIMENSION_GERAL,
          seg: localFilter.seg || SEGMENT_ALL,
        };
      }

      const nextFilter = {
        ...localFilter,
        veiculoId,
      };
      setLocalFilter(nextFilter);
      syncUrlFromFilter(nextFilter);
      setActiveCategoria(null);
      setReload((prev) => !prev);
      window.setTimeout(() => {
        transacoesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    },
    [localFilter, veiculosOptions, syncUrlFromFilter],
  );

  const filteredTransacoes = useMemo(() => {
    const rows = reportData.transacoes || [];
    if (!activeCategoria) return rows;
    return rows.filter((row) => row.categoria === activeCategoria);
  }, [reportData.transacoes, activeCategoria]);

  const tableRows = useMemo(
    () =>
      filteredTransacoes.map((row, index) => ({
        id: `${row.placa || row.veiculoPlaca || 'fleet'}-${row.data}-${row.categoria}-${index}`,
        ...row,
      })),
    [filteredTransacoes],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'data',
        headerName: 'Data',
        minWidth: 110,
        flex: 0.7,
        renderCell: (params) => formatTransactionDate(params.row.data),
      },
    ];

    if (isFleetView) {
      cols.push({
        field: 'placa',
        headerName: 'Placa',
        minWidth: 100,
        renderCell: (params) => params.row.placa || '—',
      });
    }

    cols.push(
      {
        field: 'categoria',
        headerName: 'Categoria',
        minWidth: 130,
        flex: 0.9,
        renderCell: (params) => renderCategoriaCell(params.row.categoria),
      },
      {
        field: 'descricao',
        headerName: 'Descrição',
        minWidth: 220,
        flex: 1.5,
        sortable: false,
        renderCell: (params) => renderDescricaoCell(params.row.descricao),
      },
      {
        field: 'valor',
        headerName: 'Valor',
        minWidth: 120,
        flex: 0.8,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => renderValorCell(params.row.valor),
      },
    );

    return cols;
  }, [isFleetView]);

  const onApplyFilter = (filterData) => {
    if (isPeriodInvalid(filterData.inicio, filterData.fim)) {
      setFilterError('A data de início deve ser anterior ou igual à data de fim.');
      return;
    }
    if (filterData.veiculoId && !localFilter.veiculoId) {
      fleetContextRef.current = {
        dim: localFilter.dim || DIMENSION_GERAL,
        seg: localFilter.seg || SEGMENT_ALL,
      };
    }
    const nextFilter = {
      ...filterData,
      veiculoId: filterData.veiculoId || null,
      dim: localFilter.dim || DIMENSION_GERAL,
      seg: localFilter.seg || SEGMENT_ALL,
    };
    setFilterError(null);
    setLocalFilter(nextFilter);
    syncUrlFromFilter(nextFilter);
    setActiveCategoria(null);
    setOpenFilter(false);
    setReload((prev) => !prev);
  };

  if (!canView) {
    return (
      <Box>
        <EmptyState
          title="Acesso restrito"
          message="O Relatório de Custos não está disponível para o seu usuário. Solicite acesso ao administrador."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box>
            <Typography variant="h4">Relatório de Custos</Typography>
            {contextLabel && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {contextLabel}
                </Typography>
                {showVeiculoSummary && (reportData.veiculo?.placa || veiculoPlacaFallback) && (
                  <Chip
                    size="small"
                    label={reportData.veiculo?.placa || veiculoPlacaFallback}
                    onDelete={onClearVeiculo}
                  />
                )}
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {showVeiculoSummary && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackOutlinedIcon />}
                onClick={onClearVeiculo}
              >
                Ver toda a frota
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<FilterListOutlinedIcon />}
              onClick={() => setOpenFilter(true)}
            >
              Filtros
            </Button>
          </Stack>
        </Stack>

        {fetchError && (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
            Não foi possível carregar os dados do relatório. Verifique o período
            {isFleetView ? '' : ' e o veículo'} e tente novamente.
          </Alert>
        )}

        <>
          <VeiculoSummary
            veiculo={reportData.veiculo}
            loading={loading}
            visible={showVeiculoSummary}
            onClose={onClearVeiculo}
          />

          {isFleetView && (
            <Box sx={{ pr: 2 }}>
              <SegmentDimensionTabs
                value={activeDim}
                onChange={onDimensionChange}
                disabled={loading}
              />
              {showSegmentChips && (
                <Box sx={{ mt: 1.5, mb: 1.5 }}>
                  <SegmentFilterChips
                    options={segmentChipOptions}
                    value={activeSeg}
                    loading={loading && segmentChipOptions.length === 0}
                    onChange={onSegmentChange}
                  />
                  {showEmptySegmentAlert && (
                    <Alert severity="info" variant="outlined" sx={{ mt: 1.5, borderRadius: 2 }}>
                      Nenhum segmento com custo no período selecionado.
                    </Alert>
                  )}
                </Box>
              )}
              <SegmentViewDescription
                dim={activeDim}
                seg={activeSeg}
                segmentLabel={activeSegmentLabel}
                loading={loading}
              />
            </Box>
          )}

          <KpiStrip
            kpis={reportData.kpis}
            loading={loading}
            activeCategoria={activeCategoria}
            onSelectCategoria={setActiveCategoria}
          />

          <ChartsRow
            evolucaoMensal={reportData.evolucaoMensal}
            distribuicao={reportData.distribuicao}
            totalGasto={reportData.kpis?.totalGasto ?? 0}
            loading={loading}
          />

          {isFleetView && (
            <VeiculoRankingSection
              showSegmentComparison={showSegmentComparison}
              showVehicleRanking={showVehicleRanking}
              ranking={fleetRanking}
              segmentItems={segmentItems}
              paretoInsight={paretoInsight}
              totalGasto={reportData.kpis?.totalGasto ?? 0}
              loading={loading}
              onSelectPlaca={onSelectVeiculoByPlaca}
              onSelectSegment={onSelectSegment}
            />
          )}

          <Box ref={transacoesSectionRef}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
              <InfoOutlinedIcon fontSize="small" color="action" />
              <Typography variant="h6">Transações</Typography>
              {segmentFilterLabel && (
                <Chip
                  size="small"
                  label={segmentFilterLabel}
                  onDelete={onClearSegmentFilter}
                  sx={{ ml: 0.5 }}
                />
              )}
              {activeCategoria && (
                <Typography variant="body2" color="text.secondary">
                  — filtrando por {activeCategoria}
                </Typography>
              )}
            </Stack>

            {!loading && tableRows.length === 0 ? (
              <EmptyState
                title="Nenhuma transação encontrada"
                message={
                  activeCategoria
                    ? `Não há lançamentos na categoria "${activeCategoria}" no período selecionado.`
                    : segmentFilterLabel
                      ? `Não há custos para o recorte "${segmentFilterLabel}" no período selecionado.`
                      : isFleetView
                        ? 'Não há custos registrados na frota no período selecionado.'
                        : 'Não há custos registrados para este veículo no período selecionado.'
                }
                onClearFilters={
                  activeCategoria
                    ? () => setActiveCategoria(null)
                    : segmentFilterLabel
                      ? onClearSegmentFilter
                      : undefined
                }
              />
            ) : (
              <Box sx={tableV2ContainerSx}>
                <Box sx={{ ...tableV2AreaSx, minHeight: 420 }}>
                  <DataGrid
                    rows={tableRows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    }}
                    rowHeight={52}
                    getRowHeight={() => 'auto'}
                    localeText={{
                      ...ptBR.components.MuiDataGrid.defaultProps.localeText,
                      noRowsLabel: 'Nenhuma transação encontrada',
                    }}
                    sx={{
                      ...tableV2MultilineGridSx,
                      border: 0,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </>

        {filterError && (
          <Snackbar
            open
            autoHideDuration={6000}
            onClose={() => setFilterError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="warning" onClose={() => setFilterError(null)} sx={{ width: '100%' }}>
              {filterError}
            </Alert>
          </Snackbar>
        )}

        {veiculoNotFoundError && (
          <Snackbar
            open
            autoHideDuration={6000}
            onClose={() => setVeiculoNotFoundError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              severity="warning"
              onClose={() => setVeiculoNotFoundError(null)}
              sx={{ width: '100%' }}
            >
              {veiculoNotFoundError}
            </Alert>
          </Snackbar>
        )}

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => {
            setOpenFilter(false);
            setFilterError(null);
          }}
          title="Filtros"
          fullWidth
          defaultFilter={getDefaultFilter()}
          appliedFilter={localFilter}
          confirmButtonText="Aplicar"
          fields={[
            { field: 'useDates', md: 3 },
            {
              field: 'useVeiculo',
              key: 'veiculoId',
              label: 'Veículo (opcional)',
              multiSelect: false,
              md: 12,
              xs: 12,
            },
          ]}
          onFilter={onApplyFilter}
        />
      </Stack>
    </Box>
  );
};

RelatorioVeiculoV2.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func, replace: PropTypes.func }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array.isRequired,
  veiculosOptions: PropTypes.array.isRequired,
  findVeiculosOptions: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
  veiculosOptions: getVeiculosOptions(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ findVeiculosOptions }, dispatch);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RelatorioVeiculoV2),
);
